import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { clearAllLocalData } from '@/lib/db';
import { setServerTimeFromJWT } from '@/lib/sync/serverTime';
import type { AuthUser } from '@/lib/auth';

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes → full sign-out (kiosk/tablet use)

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ error?: string; errorKey?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

async function fetchProfile(userId: string): Promise<AuthUser | null> {
  // Single round-trip: join profile + org membership together
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('id, name, role, organization_members(organization_id)')
    .eq('id', userId)
    .single();

  if (error || !profile) return null;

  const orgId = (profile.organization_members as { organization_id: string }[] | null)?.[0]?.organization_id ?? null;

  return {
    id: profile.id,
    name: profile.name,
    role: profile.role as AuthUser['role'],
    organization_id: profile.role === 'super_admin' ? null : orgId,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  // Start true so ProtectedRoute shows the splash screen while
  // Supabase restores the session. Set to false once INITIAL_SESSION resolves.
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the identity currently reflected in the local Dexie cache. Used to
  // detect user or org transitions so we can wipe cached data before the new
  // session reads anything.
  const cachedIdentity = useRef<{ userId: string; orgId: string | null } | null>(null);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      // Do not sign out while offline — the timer reschedules itself.
      // Once back online and still inactive for a full 15 minutes, sign-out fires normally.
      if (!navigator.onLine) {
        resetInactivityTimer();
        return;
      }
      supabase.auth.signOut();
    }, INACTIVITY_TIMEOUT_MS);
  }, []);

  // Debounced wrapper — avoids flooding resetInactivityTimer on every scroll/touch event
  const debouncedReset = useMemo(() => {
    return () => {
      if (debounceTimer.current) return; // already scheduled, skip
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        resetInactivityTimer();
      }, 500);
    };
  }, [resetInactivityTimer]);

  // Start inactivity timer when logged in; clear it on sign-out
  useEffect(() => {
    if (!user) {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      return;
    }
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
    events.forEach(e => window.addEventListener(e, debouncedReset, { passive: true }));
    resetInactivityTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, debouncedReset));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [user, resetInactivityTimer, debouncedReset]);

  // Supabase auth state.
  // We use onAuthStateChange as the SOLE source of truth for the session —
  // including INITIAL_SESSION which fires immediately with the stored session.
  // This avoids any blocking network call on startup: the user goes straight
  // to Login (which shows the logo animation), and is auto-redirected once
  // the profile arrives.
  useEffect(() => {
    // Safety timeout: if onAuthStateChange never fires (e.g. network error),
    // stop showing the loading splash after 3 seconds.
    const safetyTimeout = setTimeout(() => setLoading(false), 3000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // SIGNED_IN fires while login() is in-flight. We don't call setUser
        // here because login() will do window.location.replace('/') right after,
        // causing a full reload. Calling setUser here would trigger a React
        // re-render + client-side navigation that crashes before the reload fires.
        // INITIAL_SESSION (page refresh) and all other events are handled normally.
        if (event === 'SIGNED_IN') return;

        try {
          if (session?.user) {
            // Sync server/client clock from the JWT issued-at claim so offline
            // timestamps don't drift against server `updated_at` values.
            setServerTimeFromJWT(session.access_token);
            const profile = await fetchProfile(session.user.id);
            // Wipe Dexie if the identity reflected in the cache differs from
            // the incoming session (different user OR different org). This
            // stops cross-org/cross-user leaks without requiring every read
            // path to filter defensively.
            const prev = cachedIdentity.current;
            const nextOrgId = profile?.organization_id ?? null;
            if (profile && (!prev || prev.userId !== profile.id || prev.orgId !== nextOrgId)) {
              await clearAllLocalData();
              cachedIdentity.current = { userId: profile.id, orgId: nextOrgId };
            }
            setUser(profile);
          } else {
            // Signed out — flush any cached data from the previous session.
            if (cachedIdentity.current) {
              await clearAllLocalData();
              cachedIdentity.current = null;
            }
            setUser(null);
          }
        } catch {
          setUser(null);
        } finally {
          setLoading(false);
        }
      }
    );

    return () => { clearTimeout(safetyTimeout); subscription.unsubscribe(); };
  }, []);

  const login = useCallback(async (
    email: string,
    password: string,
  ): Promise<{ error?: string; errorKey?: string }> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      // Return a translation key for the common case; fall back to the
      // server-supplied message for unusual errors (preserves diagnostic
      // detail while keeping the shop-floor UI localised).
      if (error.message.toLowerCase().includes('invalid')) {
        return { errorKey: 'auth:error.invalid_credentials' };
      }
      return { error: error.message };
    }

    return {};
  }, []);

  const logout = useCallback(async () => {
    setUser(null);
    await clearAllLocalData();
    cachedIdentity.current = null;
    await supabase.auth.signOut();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
